import React, { createContext, useState, useEffect } from "react";
import { ShowToast, Severty } from "../helper/toast";
import { storesAPI, productsAPI } from "../services/api";

export const StoreContext = createContext();
export const StoreProvider = ({ children }) => {
  const [stores, setStores] = useState([]);
  const [featuredStores, setFeaturedStores] = useState([]);
  const [currentStore, setCurrentStore] = useState(null);
  const [loading, setLoading] = useState(false);

  // const { globalSearch } = useChat();
  const [filters, setFilters] = useState({
    category: "All",
    sortBy: "newest",
    searchQuery: "",
    page: 1,
    limit: 10,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0,
  });

  // Product-related state
  const [vendorProducts, setVendorProducts] = useState([]);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [vendorDetail, setVendorDetail] = useState(null);
  const [productCategories, setProductCategories] = useState([]);
  const [productFilters, setProductFilters] = useState({
    category: "",
    vendorId: "",
  });

  // Cart state persisted to localStorage for quick hydration
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem("cart");
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      return null;
    }
  });

  const refreshCart = async () => {
    try {
      const response = await storesAPI.getCartItems();
      const nextCart = response?.data || null;
      setCart(nextCart);
      try {
        localStorage.setItem("cart", JSON.stringify(nextCart));
      } catch (e) {}
      return response;
    } catch (error) {
      return { success: false, error };
    }
  };

  // Request Refund
  const requestRefund = async (data) => {
    try {
      const response = await storesAPI.requestRefund(data);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const loadStores = async (params = {}) => {
    setLoading(true);
    try {
      const queryParams = {
        page: Number(pagination.page),
        limit: Number(pagination.limit),
        category: filters.category !== "All" ? filters.category : undefined,
        sort: filters.sortBy,
        search: filters.searchQuery || undefined,
        ...params,
      };

      const response = await storesAPI.getAllStores(queryParams);

      if (response.status === 200 && response.data) {
        setStores(response.data.stores || response.data);
        return { status: response.status, data: response.data };
      } else {
        ShowToast(response.message || "Failed to load stores", Severty.ERROR);
        return {
          success: false,
          error: response.message || "Failed to load stores",
        };
      }
    } catch (error) {
      const errorMessage =
        error.message ||
        error.error ||
        "Failed to load stores. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const loadFeaturedStores = async () => {
    try {
      const response = await storesAPI.getFeaturedStores();

      if (response.status === 200 && response.data) {
        setFeaturedStores(response.data.stores || response.data);
        return { success: true, data: response };
      } else {
        console.warn("Failed to load featured stores:", response.message);
        return { success: false, error: response.message };
      }
    } catch (error) {
      console.warn("Error loading featured stores:", error.message);
      return { success: false, error: error.message };
    }
  };

  const loadStoreById = async (storeId) => {
    setLoading(true);
    try {
      const response = await storesAPI.getStoreById(storeId);

      if (response.status === 200 && response.data) {
        setCurrentStore(response.data);
        return { success: true, data: response };
      } else {
        ShowToast(response.message || "Store not found", Severty.ERROR);
        return { success: false, error: response.message || "Store not found" };
      }
    } catch (error) {
      const errorMessage =
        error.message || error.error || "Failed to load store details.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const createStore = async (storeData) => {
    setLoading(true);
    try {
      const response = await storesAPI.createStore(storeData);

      if (response.status === 200 || response.status === 201) {
        // Refresh stores list after creation
        await loadStores();
        ShowToast(
          response.message || "Store created successfully!",
          Severty.SUCCESS
        );
        return { success: true, data: response };
      } else {
        ShowToast(response.message || "Failed to create store", Severty.ERROR);
        return {
          success: false,
          error: response.message || "Failed to create store",
        };
      }
    } catch (error) {
      const errorMessage =
        error.message ||
        error.error ||
        "Failed to create store. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const updateStore = async (storeId, storeData) => {
    setLoading(true);
    try {
      const response = await storesAPI.updateStore(storeId, storeData);

      if (response.status === 200) {
        // Update the store in the current list
        setStores((prev) =>
          prev.map((store) =>
            store.id === storeId ? { ...store, ...response.data } : store
          )
        );
        ShowToast(
          response.message || "Store updated successfully!",
          Severty.SUCCESS
        );
        return { success: true, data: response };
      } else {
        ShowToast(response.message || "Failed to update store", Severty.ERROR);
        return {
          success: false,
          error: response.message || "Failed to update store",
        };
      }
    } catch (error) {
      const errorMessage =
        error.message ||
        error.error ||
        "Failed to update store. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const deleteStore = async (storeId) => {
    setLoading(true);
    try {
      const response = await storesAPI.deleteStore(storeId);

      if (response.status === 200) {
        // Remove the store from the current list
        setStores((prev) => prev.filter((store) => store.id !== storeId));
        ShowToast(
          response.message || "Store deleted successfully!",
          Severty.SUCCESS
        );
        return { success: true, data: response };
      } else {
        ShowToast(response.message || "Failed to delete store", Severty.ERROR);
        return {
          success: false,
          error: response.message || "Failed to delete store",
        };
      }
    } catch (error) {
      const errorMessage =
        error.message ||
        error.error ||
        "Failed to delete store. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const searchStores = async (searchQuery) => {
    setLoading(true);
    try {
      const response = await storesAPI.searchStores(searchQuery);

      if (response.status === 200 && response.data) {
        setStores(response.data.stores || response.data);
        return { success: true, data: response };
      } else {
        ShowToast(response.message || "No stores found", Severty.INFO);
        return { success: false, error: response.message || "No stores found" };
      }
    } catch (error) {
      const errorMessage =
        error.message || error.error || "Search failed. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const getFeaturedProducts = async (params) => {
    try {
      const response = await productsAPI.getFeaturedProducts(params);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const updateFilters = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const updatePagination = (newPagination) => {
    setPagination((prev) => ({ ...prev, ...newPagination }));
  };

  const clearCurrentStore = () => {
    setCurrentStore(null);
  };

  // ------------ products -------------
  const loadVendorProducts = async (vendorId, params = {}) => {
    setLoading(true);
    try {
      const response = await productsAPI.getVendorProducts(vendorId, params);

      if (response.status === 200 && response.data) {
        setVendorProducts(response.data.data || []);
        setVendorDetail(response.data.vendorDetail || null);
        setProductCategories(response.data.categories || []);
        setProductFilters(response.data.filter || { vendorId, category: "" });
        return { success: true, data: response.data };
      } else {
        ShowToast(
          response.message || "Failed to load vendor products",
          Severty.ERROR
        );
        return {
          success: false,
          error: response.message || "Failed to load vendor products",
        };
      }
    } catch (error) {
      const errorMessage =
        error.message ||
        error.error ||
        "Failed to load vendor products. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const loadProductById = async (productId) => {
    setLoading(true);
    try {
      const response = await productsAPI.getProductById(productId);

      if (response.status === 200 && response.data) {
        setCurrentProduct(response.data.data || response.data);
        return { success: true, data: response.data.data };
      } else {
        ShowToast(response.message || "Product not found", Severty.ERROR);
        return {
          success: false,
          error: response.message || "Product not found",
        };
      }
    } catch (error) {
      const errorMessage =
        error.message ||
        error.error ||
        "Failed to load product details. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const searchProducts = async (searchQuery, params = {}) => {
    setLoading(true);
    try {
      const response = await productsAPI.searchProducts(searchQuery, params);

      if (response.status === 200 && response.data) {
        setVendorProducts(response.data.products || response.data);
        return { success: true, data: response.data };
      } else {
        ShowToast(response.message || "No products found", Severty.INFO);
        return {
          success: false,
          error: response.message || "No products found",
        };
      }
    } catch (error) {
      const errorMessage =
        error.message ||
        error.error ||
        "Product search failed. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const likeProduct = async (productId) => {
    try {
      const response = await productsAPI.likeProduct(productId);
      if (response.status === 200) {
        return { success: true, data: response.data };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || "like the product failed",
      };
    }
  };

  const revokeLike = async (productId) => {
    try {
      const response = await productsAPI.revokeLike(productId);
      if (response.status === 200) {
        return { success: true, data: response.data };
      }
    } catch (error) {
      ShowToast(error.message || "revoke like product failed", Severty.INFO);
      return {
        success: false,
        error: error.message || "revoke like  product failed",
      };
    }
  };

  const updateProductFilters = (newFilters) => {
    setProductFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const clearCurrentProduct = () => {
    setCurrentProduct(null);
  };

  const clearVendorProducts = () => {
    setVendorProducts([]);
    setVendorDetail(null);
    setProductCategories([]);
  };

  //  -------------- orders -------------

  const placeOrder = async (orderData) => {
    const response = await storesAPI.placeOrder(orderData);
    return response;
  };

  const reOrder = async (orderId) => {
    const response = await storesAPI.reOrder(orderId);
    return response;
  };

  const leaveFeedback = async (feedbackData) => {
    try {
      const response = await storesAPI.leaveFeedback(feedbackData);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const loadOrders = async () => {
    const response = await storesAPI.getOrders();
    return response;
  };

  const loadOrderById = async (orderId) => {
    const response = await storesAPI.getOrderById(orderId);
    return response;
  };

  const addCartItem = async (itemId, quantity) => {
    const response = await storesAPI.addCartItem(itemId, quantity);
    if (response?.status === 200) {
      await refreshCart();
    }
    return response;
  };

  const deleteCartItem = async (itemId, quantity) => {
    const response = await storesAPI.deleteCartItem(itemId, quantity);
    if (response?.status === 200) {
      await refreshCart();
    }
    return response;
  };

  const loadCartItems = async () => {
    const response = await storesAPI.getCartItems();
    return response;
  };

  // -------------- address -------------
  const loadAddress = async () => {
    const response = await storesAPI.getAddress();
    return response;
  };

  const deleteAddress = async (addressId) => {
    const response = await storesAPI.deleteAddress(addressId);
    return response;
  };

  const addAddress = async (addressData) => {
    const response = await storesAPI.addAddress(addressData);
    return response;
  };

  return (
    <StoreContext.Provider
      value={{
        // State
        stores,
        featuredStores,
        currentStore,
        loading,
        filters,
        pagination,

        // Product State
        vendorProducts,
        currentProduct,
        vendorDetail,
        productCategories,
        productFilters,

        // Actions
        loadStores,
        loadOrders,
        loadOrderById,
        addCartItem,
        loadCartItems,
        refreshCart,
        loadAddress,
        deleteAddress,
        deleteCartItem,
        addAddress,
        loadFeaturedStores,
        loadStoreById,
        createStore,
        updateStore,
        deleteStore,
        searchStores,
        updateFilters,
        updatePagination,
        clearCurrentStore,
        leaveFeedback,

        // Product Actions
        loadVendorProducts,
        loadProductById,
        searchProducts,
        updateProductFilters,
        clearCurrentProduct,
        clearVendorProducts,
        likeProduct,
        revokeLike,
        placeOrder,
        reOrder,
        getFeaturedProducts,
        requestRefund,

        // Setters
        cart,
        setCart,
        setStores,
        setFeaturedStores,
        setCurrentStore,
        setLoading,
        setFilters,
        setPagination,
        setVendorProducts,
        setCurrentProduct,
        setVendorDetail,
        setProductCategories,
        setProductFilters,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
};

// Custom hook to use the store context
export const useStore = () => {
  const context = React.useContext(StoreContext);
  if (context === undefined) {
    throw new Error("useStore must be used within a StoreProvider");
  }
  return context;
};
